/**
 * The simulator panel.
 *
 * Design doc: 2026-08-11-mobile-simulator-integration §16.2 (layer stack), §16.1
 * (agency model), §16.5 (motion).
 *
 * Every decision about *what* to show lives in `simulatorPanelState.ts`; this
 * file decides only how to draw it. That split is why the panel can be reasoned
 * about at all: "a stale frame must never render as live" is a rule, not a
 * rendering detail, and it is asserted there without a DOM.
 *
 * The layer stack in §16.2 is described as load-bearing and copied exactly. The
 * ordering matters for a specific reason: the touch surface sits ABOVE the bezel
 * overlay, so a tap near the device edge reaches the app rather than the
 * decoration, and the edge-capture region owns its own hit area so OS edge
 * gestures do not steal from the app.
 */

import {
  canTakeControl,
  copyFor,
  isPending,
  isStreamLive,
  offersRetry,
  type SimulatorPanelState,
} from "./simulatorPanelState";

/*
 * The device screen uses the --sim-device-screen token rather than a raw colour
 * class. A real iOS screen is the same shade in both themes — it is the physical
 * device, not app chrome — but the repo's theme guard is right to refuse raw
 * colours anyway: a token keeps the exception visible and gives a future bezel
 * or OLED change one place to happen.
 *
 * The guard matches line by line, so naming the raw class even in a comment
 * trips it. That is the correct trade: a guard that parsed context could be
 * argued with.
 */

export interface SimulatorPanelProps {
  state: SimulatorPanelState;
  /** Device points, e.g. 402x874. The frame scales to its container, not the viewport. */
  deviceWidthPt: number;
  deviceHeightPt: number;
  streamUrl?: string | undefined;
  lastFrameAtMs?: number | null;
  nowMs?: number;
  viewportWidthPx?: number;
  hasControl?: boolean;
  retrySeconds?: number;
  onTakeControl?: () => void;
  onRetry?: () => void;
}

/**
 * A live dot, but only when the stream is genuinely live.
 *
 * Driven by `isStreamLive`, never by the state alone: a wedged simulator is
 * still in the `live` state and still has a last frame on screen. Showing a live
 * indicator over a frozen picture is the failure this exists to prevent.
 */
function LiveDot({ live }: { live: boolean }) {
  if (!live) return null;
  return (
    <span
      data-testid="sim-live-dot"
      aria-label="Stream is live"
      className="inline-flex h-2 w-2 rounded-full bg-success"
    />
  );
}

export function SimulatorPanel(props: SimulatorPanelProps) {
  const {
    state,
    deviceWidthPt,
    deviceHeightPt,
    streamUrl,
    lastFrameAtMs = null,
    nowMs = Date.now(),
    viewportWidthPx = 1280,
    hasControl = false,
    retrySeconds,
    onTakeControl,
    onRetry,
  } = props;

  const copy = copyFor(state);
  const live = isStreamLive({ state, lastFrameAtMs, nowMs });
  const showTakeControl = canTakeControl({ viewportWidthPx, state });
  const pending = isPending(state);

  return (
    <section
      data-testid="simulator-panel"
      data-state={state}
      aria-label="iOS simulator"
      // svh, never vh (DESIGN.md): vh is wrong on mobile browsers with dynamic
      // toolbars, and this panel is docked as often as it is full-screen.
      className="relative isolate flex max-h-[80svh] flex-col gap-3"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <LiveDot live={live} />
          <h2 className="text-sm font-medium">{copy.title}</h2>
        </div>

        {/*
          Take control is hidden below md rather than shrunk (§16.1, D1). A 402pt
          device in a 390px viewport renders app controls at ~38px, so the 48px
          touch contract cannot be met by scaling.
        */}
        {showTakeControl ? (
          <button
            type="button"
            data-testid="sim-take-control"
            onClick={onTakeControl}
            className="hidden md:inline-flex text-sm"
          >
            {hasControl ? "Release control" : "Take control"}
          </button>
        ) : null}
      </header>

      {/* Frame wrapper: the device scales to THIS, not to the viewport. */}
      <div
        data-testid="sim-frame"
        className="relative isolate [container-type:size] overflow-hidden rounded-2xl bg-[var(--sim-device-screen)]"
        style={{ aspectRatio: `${deviceWidthPt} / ${deviceHeightPt}` }}
      >
        {/* z-0 — stream */}
        {live && streamUrl ? (
          <img
            data-testid="sim-stream"
            src={streamUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div
            data-testid="sim-placeholder"
            // The placeholder breathes while a capture is pending. `animate-ghost-pulse`,
            // not `animate-pulse`: duty-cycled and motion-safe gated. DESIGN.md -> "Motion".
            className={`absolute inset-0 flex items-center justify-center bg-[var(--sim-device-screen)] text-sm text-[var(--sim-device-screen-foreground)] ${
              pending ? "motion-safe:animate-ghost-pulse" : ""
            }`}
          >
            {copy.title}
          </div>
        )}

        {/* z-10 — bezel overlay, decoration only */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10"
          data-testid="sim-bezel"
        />

        {/* z-10 — edge capture, its own hit region for OS edge gestures */}
        <div
          aria-hidden
          data-testid="sim-edge-capture"
          className="absolute inset-x-0 bottom-0 z-10 h-4 touch-none"
        />

        {/*
          z-20 — touch surface, ABOVE the bezel so a tap near the edge reaches
          the app rather than the decoration. Only interactive when the human
          has taken control; otherwise the device is inert to touch and the
          agent drives it.
        */}
        <div
          data-testid="sim-touch-surface"
          role="application"
          tabIndex={hasControl ? 0 : -1}
          aria-disabled={!hasControl}
          aria-label="Simulator touch surface. Click to focus, then type to send keys to the device."
          className={`absolute inset-0 z-20 focus-visible:ring-2 focus-visible:ring-ring ${
            hasControl ? "" : "pointer-events-none"
          }`}
        />
      </div>

      {/*
        Remediation is shown, not hidden behind a details toggle: a user stuck
        here needs the command, and §16.8 requires it be named exactly.
      */}
      {copy.remediation ? (
        <p data-testid="sim-remediation" className="text-sm text-muted-foreground">
          {copy.remediation}
        </p>
      ) : null}

      {state === "crash-retrying" && typeof retrySeconds === "number" ? (
        // tabular-nums or the countdown jitters as digits change width (§16.4).
        <p data-testid="sim-retry-countdown" className="text-sm tabular-nums">
          Trying again in {retrySeconds}s
        </p>
      ) : null}

      {offersRetry(state) ? (
        <button type="button" data-testid="sim-retry" onClick={onRetry} className="text-sm">
          Try again
        </button>
      ) : null}
    </section>
  );
}
